import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'tr' | 'fr' | 'es' | 'ar' | 'de' | 'it' | 'ru';

// Define EN translations first to use as fallback
const enTranslations = {
  demo: 'Demo',
  pricing: 'Pricing',
  blog: 'Blog',
  signIn: 'Sign In',
  startFree: 'Start Free',
  hero: 'Script Analysis',
  heroSub: 'Accelerated with AI',
  heroDesc: 'Analyze your works in detail with our AI-powered platform. Discover characters, plot, dialogue and more with professional evaluations.',
  startNow: 'Start Now',
  seeDemo: 'See Demo',
  fastAnalysis: 'Fast Analysis',
  fastAnalysisDesc: 'Analyze your works in minutes. Get detailed reports thanks to AI technology.',
  detailedReports: 'Detailed Reports',
  detailedReportsDesc: 'Get in-depth information about character analysis, plot, dialogue quality and more.',
  secure: 'Secure & Private',
  secureDesc: 'Your data is completely safe and private. Only you and April AI can access your analysis data.',
  firstFree: 'First Analysis Free',
  firstFreeDesc: 'Analyze one work comprehensively without a credit card. Get started now.',
  tryFree: 'Try Free',
  copyright: '© 2025 April AI. All rights reserved.',
  back: 'Back',
  analyzeTitle: 'Analyses',
  viewAnalyses: 'Go to Analyses',
  // Upload Page
  newAnalysis: 'Create New Analysis',
  newAnalysisDesc: 'Upload your script and start the AI-powered analysis process',
  uploadPdf: 'Upload PDF File',
  dragDropPdf: 'Drag and drop your PDF file here',
  orClickToSelect: 'or click to select a file',
  selectFile: 'Select File',
  maxFileSize: 'Maximum file size: 50 MB',
  changeFile: 'Change file',
  fileTooLarge: 'File is too large. Maximum file size: 50 MB',
  contentType: 'Content Type',
  filmScript: 'Film Script',
  seriesScript: 'Series Script',
  analysisOptions: 'Analysis Options',
  generateReport: 'Generate Report',
  generateCharacterBreakdown: 'Character Breakdown',
  sequentialBreakdown: 'Sequential Breakdown',
  reportLanguage: 'Report Language',
  uploading: 'Uploading...',
  uploadAndAnalyze: 'Upload and Analyze',
  analysisCompleted: 'Analysis Completed',
  analysisCompletedDesc: 'Analysis and your selected additional outputs have been created. Click the button below to view analysis details.',
  viewAnalysis: 'View Analysis',
  reports: 'Reports',
  loading: 'Loading',
  processing: 'Processing',
  analyzing: 'Analyzing, please wait...',
  // Additional upload page translations
  file: 'File',
  analysis: 'Analysis',
  summary: 'Summary',
  options: 'Options',
  startAnalysis: 'Start Analysis',
  series: 'Series',
  film: 'Film',
  character: 'Character',
  pleaseSelectFile: 'Please select a file',
  pleaseSelectContentType: 'Please select content type',
  pleaseSelectAtLeastOneOutput: '⚠️ Please select at least one output!',
  readingPdf: '📄 Reading PDF file...',
  creatingCharacterBreakdown: '🎭 Creating Character Breakdown...',
  uploadFailed: 'Upload failed',
  backendNotResponding: 'Backend not responding (timeout). Please check if backend is running.',
  backendConnectionError: 'Backend connection error. Please check if backend is running. (http://localhost:8000)',
  authorizationError: 'Authorization error. Please login again.',
  invalidFileFormat: 'Invalid file format. Please upload a PDF file.',
  allOperationsCompleted: 'All operations completed successfully!',
  viewAnalysisResults: '📊 View Analysis Results',
  // Navbar
  faq: 'FAQ',
  newProject: 'New Project',
  tokenLabel: 'Tokens',
  admin: 'Admin',
  logout: 'Logout',
  // Admin Page
  userListFailed: 'Failed to load user list',
  statsFailed: 'Failed to load statistics',
  userUpdated: 'User updated',
  userUpdateFailed: 'User update failed',
  adminPanel: 'Admin Panel',
  users: 'Users',
  project: 'Project',
  noEmail: 'No email',
  noUsers: 'No users found',
  userInfo: 'User Info',
  username: 'Username',
  fullName: 'Full Name',
  email: 'Email',
  roles: 'Roles',
  rolesHint: 'comma separated',
  newPassword: 'New Password',
  leaveBlankForNoChange: 'leave blank for no change',
  saving: 'Saving',
  save: 'Save',
  selectUserFromLeft: 'Select a user from the left list',
  userStatistics: 'User Statistics',
  totalProjects: 'Total Projects',
  averageScore: 'Average Score',
  highestScore: 'Highest Score',
  lowestScore: 'Lowest Score',
  statsNotFound: 'No statistics found',
  statsWillAppear: 'Statistics will appear here',
  // Dashboard
  deleteConfirm: 'Are you sure you want to delete {name}?',
  projectDeleted: 'Project deleted successfully',
  projectDeleteFailed: 'Failed to delete project',
  sortByDate: 'Sort by Date',
  sortByScore: 'Sort by Score',
  sortByName: 'Sort by Name',
  search: 'Search',
  myProjects: 'My Projects',
  projects: 'Projects',
  sort: 'Sort',
  workName: 'Work Name',
  type: 'Type',
  uploadDate: 'Upload Date',
  overallScore: 'Overall Score',
  status: 'Status',
  actions: 'Actions',
  analyzed: 'Analyzed',
  noProjectsFound: 'No projects found',
  // FAQ
  faqTitle: 'Frequently Asked Questions',
  faqSubtitle: 'Find answers to common questions',
  // Login
  loginError: 'Login failed. Please check your credentials.',
  login: 'Login',
  accountLogin: 'Login to your account',
  password: 'Password',
  loginInProgress: 'Logging in...',
  loginButton: 'Log In',
  // Pricing
  freePlan: 'Free Plan',
  freePlanDesc: 'Perfect for trying',
  freeFeatures: ['1 Free Analysis', 'Basic Report', 'Email Support'],
  basicPlan: 'Basic',
  basicPlanDesc: 'For individuals',
  basicFeatures: ['10 Analysis/month', 'Detailed Reports', 'Priority Support'],
  getStarted: 'Get Started',
  proPlan: 'Pro',
  proPlanDesc: 'For professionals',
  proFeatures: ['Unlimited Analysis', 'Advanced Reports', '24/7 Support'],
  enterprisePlan: 'Enterprise',
  enterprisePlanDesc: 'For teams',
  enterpriseFeatures: ['Unlimited Everything', 'Custom Integration', 'Dedicated Support'],
  pricingTitle: 'Simple, Transparent Pricing',
  pricingSubtitle: 'Choose the plan that suits your needs',
  monthly: 'Monthly',
  yearly: 'Yearly',
  perMonth: '/month',
  perYear: '/year',
  mostPopular: 'Most Popular',
  // Project Detail
  deleteConfirmMessage: 'Are you sure you want to delete "{name}"?',
  projectDetails: 'Project Details',
  backToDashboard: 'Back to Dashboard',
  projectNotFound: 'Project not found',
  overview: 'Overview',
  generalScore: 'General Score',
  format: 'Format',
  logline: 'Logline',
  genisOzet: 'Summary',
  loadingAnalysis: 'Loading analysis...',
  noAnalysisSections: 'No analysis sections available',
  downloadFullAnalysisPdf: 'Download Full Analysis PDF',
  characterBreakdownNotCreated: 'Character breakdown not created yet',
  generatingCharacterBreakdown: 'Generating character breakdown...',
  characterBreakdownCreated: 'Character Breakdown Created',
  characterBreakdownCreatedMessage: 'Character breakdown has been successfully created',
  characterBreakdownFailed: 'Failed to generate character breakdown',
  generating: 'Generating...',
  generateCharacterBreakdownButton: 'Generate Character Breakdown',
  characterBreakdownTitle: 'Character Breakdown',
  sceneReplica: 'Scenes / Replicas',
  characterSceneCount: 'Total Scenes',
  characterInteractionBreakdown: 'Character Interactions',
  replicas: 'Replicas',
  scene: 'Scene',
  noDialogue: 'No dialogue',
  voiceoverTitle: 'Voiceover',
  simpleScriptReading: 'Simple Script Reading',
  simpleScriptReadingDesc: 'Convert your script to audio',
  reading: 'Reading...',
  readScript: 'Read Script',
  goToListeningPage: 'Go to Listening Page',
  projectTypeLabel: 'Project Type',
  deleting: 'Deleting...',
  deleteProjectLabel: 'Delete Project',
  analysisSections: 'Analysis Sections',
  report: 'Report',
  scoring: 'Scoring',
  characters: 'Characters',
  voiceover: 'Voiceover',
  close: 'Close',
  processCompleted: 'Process Completed',
  processCompletedMessage: 'Process completed successfully',
  goToAnalysis: 'Go to Analysis',
  sequentialBreakdownNotCreated: 'Sequential breakdown not created yet',
  generateSequentialBreakdownButton: 'Generate Sequential Breakdown',
  sequentialBreakdownCreated: 'Sequential Breakdown Created',
  sequentialBreakdownCreatedMessage: 'Sequential breakdown has been created successfully.',
  sequentialBreakdownFailed: 'Failed to generate sequential breakdown',
  exportExcel: 'Export Excel',
  downloadReport: 'Download Report',
  downloadFailed: 'Download failed',
};

const translations = {
  en: enTranslations,
  tr: {
    demo: 'Demo',
    pricing: 'Fiyatlandırma',
    blog: 'Blog',
    signIn: 'Giriş Yap',
    startFree: 'Ücretsiz Başla',
    hero: 'Senaryo Analizi',
    heroSub: 'AI ile Hızlandırılmış',
    heroDesc: 'Yapay zekaya destekli platform ile eserlerinizi detaylı analiz edin. Karakterler, olay örgüsü, diyalog ve daha fazlasını profesyonel değerlendirmelerle keşfedin.',
    startNow: 'Hemen Başla',
    seeDemo: 'Demo Gör',
    fastAnalysis: 'Hızlı Analiz',
    fastAnalysisDesc: 'Eserlerinizi dakikalar içinde kapsamlı analiz edin. AI teknolojisi sayesinde detaylı raporlar alın.',
    detailedReports: 'Detaylı Raporlar',
    detailedReportsDesc: 'Karakter analizi, olay örgüsü, diyalog kalitesi ve daha fazlası hakkında derinlemesine bilgi alın.',
    secure: 'Güvenli ve Gizli',
    secureDesc: 'Verileriniz tamamen güvenli ve özel. Sadece siz ve April AI analiz verilerinize erişebilir.',
    firstFree: 'İlk Analiz Ücretsiz',
    firstFreeDesc: 'Hiçbir kredi kartı gerekmeden bir eseri tam kapsamlı analiz edin. Şimdi başlayın.',
    tryFree: 'Ücretsiz Deneyin',
    copyright: '© 2025 April AI. Tüm hakları saklıdır.',
    back: 'Geri',
    analyzeTitle: 'Analizler',
    viewAnalyses: 'Analizler\'e Git',
    // Upload Page
    newAnalysis: 'Yeni Analiz Oluştur',
    newAnalysisDesc: 'Senaryonuzu yükleyin ve yapay zeka destekli analiz sürecini başlatın',
    uploadPdf: 'PDF Dosyası Yükle',
    dragDropPdf: 'PDF dosyanızı sürükleyip bırakın',
    orClickToSelect: 'veya dosya seçmek için tıklayın',
    selectFile: 'Dosya Seç',
    maxFileSize: 'Maksimum dosya boyutu: 50 MB',
    changeFile: 'Dosyayı değiştir',
    fileTooLarge: 'Dosya çok büyük. Maksimum dosya boyutu: 50 MB',
    contentType: 'İçerik Türü',
    filmScript: 'Film Senaryosu',
    seriesScript: 'Dizi Senaryosu',
    analysisOptions: 'Analiz Seçenekleri',
    generateReport: 'Rapor Oluştur',
    generateCharacterBreakdown: 'Karakter Dökümü',
    sequentialBreakdown: 'Sıralı Döküm',
    reportLanguage: 'Rapor Dili',
    uploading: 'Yükleniyor...',
    uploadAndAnalyze: 'Yükle ve Analiz Et',
    analysisCompleted: 'Analiz Tamamlandı',
    analysisCompletedDesc: 'Analiz ve seçtiğiniz ek çıktılar oluşturuldu. Analiz detaylarını görmek için aşağıdaki butona tıklayın.',
    viewAnalysis: 'Analizi Görüntüle',
    reports: 'Raporlar',
    loading: 'Yükleniyor',
    processing: 'İşleniyor',
    analyzing: 'Analiz ediliyor, lütfen bekleyin...',
    // Additional upload page translations
    file: 'Dosya',
    analysis: 'Analiz',
    summary: 'Özet',
    options: 'Seçenekler',
    startAnalysis: 'Analizi Başlat',
    series: 'Dizi',
    film: 'Film',
    character: 'Karakter',
    pleaseSelectFile: 'Lütfen bir dosya seçin',
    pleaseSelectContentType: 'Lütfen içerik türünü seçin',
    pleaseSelectAtLeastOneOutput: '⚠️ Lütfen en az bir çıktı seçin!',
    readingPdf: '📄 PDF dosyası okunuyor...',
    creatingCharacterBreakdown: '🎭 Karakter Dökümü oluşturuluyor...',
    uploadFailed: 'Yükleme başarısız',
    backendNotResponding: 'Backend yanıt vermiyor (timeout). Backend çalışıyor mu kontrol edin.',
    backendConnectionError: 'Backend bağlantı hatası. Backend çalışıyor mu kontrol edin. (http://localhost:8000)',
    authorizationError: 'Yetkilendirme hatası. Lütfen tekrar giriş yapın.',
    invalidFileFormat: 'Geçersiz dosya formatı. Lütfen PDF dosyası yükleyin.',
    allOperationsCompleted: 'Tüm işlemler başarıyla tamamlandı!',
    viewAnalysisResults: '📊 Analiz Sonuçlarını Gör',
    // Navbar
    faq: 'SSS',
    newProject: 'Yeni Proje',
    tokenLabel: 'Token',
    admin: 'Admin',
    logout: 'Çıkış',
    // Admin Page
    userListFailed: 'Kullanıcı listesi yüklenemedi',
    statsFailed: 'İstatistikler yüklenemedi',
    userUpdated: 'Kullanıcı güncellendi',
    userUpdateFailed: 'Kullanıcı güncellenemedi',
    adminPanel: 'Yönetim Paneli',
    users: 'Kullanıcılar',
    project: 'Proje',
    noEmail: 'E-posta yok',
    noUsers: 'Kullanıcı bulunamadı',
    userInfo: 'Kullanıcı Bilgileri',
    username: 'Kullanıcı Adı',
    fullName: 'Tam Ad',
    email: 'E-posta',
    roles: 'Roller',
    rolesHint: 'virgülle ayırarak',
    newPassword: 'Yeni Şifre',
    leaveBlankForNoChange: 'değiştirmemek için boş bırakın',
    saving: 'Kaydediliyor',
    save: 'Kaydet',
    selectUserFromLeft: 'Sol listeden bir kullanıcı seçin',
    userStatistics: 'Kullanıcı İstatistikleri',
    totalProjects: 'Toplam Proje',
    averageScore: 'Ortalama Puan',
    highestScore: 'En Yüksek Puan',
    lowestScore: 'En Düşük Puan',
    statsNotFound: 'İstatistik bulunamadı',
    statsWillAppear: 'İstatistikler burada görünecek',
    // Dashboard
    deleteConfirm: '"{name}" projesini silmek istediğinizden emin misiniz?',
    projectDeleted: 'Proje başarıyla silindi',
    projectDeleteFailed: 'Proje silinemedi',
    sortByDate: 'Tarihe Göre Sırala',
    sortByScore: 'Puana Göre Sırala',
    sortByName: 'İsme Göre Sırala',
    search: 'Ara',
    myProjects: 'Projelerim',
    projects: 'Proje',
    sort: 'Sırala',
    workName: 'Eser Adı',
    type: 'Tür',
    uploadDate: 'Yüklenme Tarihi',
    overallScore: 'Genel Puan',
    status: 'Durum',
    actions: 'İşlemler',
    analyzed: 'Analiz Edildi',
    noProjectsFound: 'Proje bulunamadı',
    // FAQ
    faqTitle: 'Sıkça Sorulan Sorular',
    faqSubtitle: 'Yaygın soruların cevaplarını bulun',
    // Login
    loginError: 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.',
    login: 'Giriş Yap',
    accountLogin: 'Hesabınıza giriş yapın',
    password: 'Şifre',
    loginInProgress: 'Giriş yapılıyor...',
    loginButton: 'Giriş Yap',
    // Pricing
    freePlan: 'Ücretsiz Plan',
    freePlanDesc: 'Denemek için ideal',
    freeFeatures: ['1 Ücretsiz Analiz', 'Temel Rapor', 'E-posta Destek'],
    basicPlan: 'Temel',
    basicPlanDesc: 'Bireyler için',
    basicFeatures: ['10 Analiz/ay', 'Detaylı Raporlar', 'Öncelikli Destek'],
    getStarted: 'Başla',
    proPlan: 'Pro',
    proPlanDesc: 'Profesyoneller için',
    proFeatures: ['Sınırsız Analiz', 'Gelişmiş Raporlar', '7/24 Destek'],
    enterprisePlan: 'Kurumsal',
    enterprisePlanDesc: 'Ekipler için',
    enterpriseFeatures: ['Sınırsız Her Şey', 'Özel Entegrasyon', 'Özel Destek'],
    pricingTitle: 'Basit ve Şeffaf Fiyatlandırma',
    pricingSubtitle: 'İhtiyacınıza uygun planı seçin',
    monthly: 'Aylık',
    yearly: 'Yıllık',
    perMonth: '/ay',
    perYear: '/yıl',
    mostPopular: 'En Popüler',
    // Project Detail
    deleteConfirmMessage: '"{name}" projesini silmek istediğinizden emin misiniz?',
    projectDetails: 'Proje Detayları',
    backToDashboard: 'Panele Dön',
    projectNotFound: 'Proje bulunamadı',
    overview: 'Genel Bakış',
    generalScore: 'Genel Puan',
    format: 'Format',
    logline: 'Logline',
    genisOzet: 'Geniş Özet',
    loadingAnalysis: 'Analiz yükleniyor...',
    noAnalysisSections: 'Analiz bölümü bulunamadı',
    downloadFullAnalysisPdf: 'Tam Analiz PDF İndir',
    characterBreakdownNotCreated: 'Karakter dökümü henüz oluşturulmadı',
    generatingCharacterBreakdown: 'Karakter dökümü oluşturuluyor...',
    characterBreakdownCreated: 'Karakter Dökümü Oluşturuldu',
    characterBreakdownCreatedMessage: 'Karakter dökümü başarıyla oluşturuldu',
    characterBreakdownFailed: 'Karakter dökümü oluşturulamadı',
    generating: 'Oluşturuluyor...',
    generateCharacterBreakdownButton: 'Karakter Dökümü Oluştur',
    characterBreakdownTitle: 'Karakter Dökümü',
    sceneReplica: 'Sahne / Replik',
    characterSceneCount: 'Toplam Sahne',
    characterInteractionBreakdown: 'Karakter Etkileşimleri',
    replicas: 'Replikler',
    scene: 'Sahne',
    noDialogue: 'Diyalog yok',
    voiceoverTitle: 'Seslendirme',
    simpleScriptReading: 'Basit Senaryo Okuma',
    simpleScriptReadingDesc: 'Senaryonuzu sese dönüştürün',
    reading: 'Okunuyor...',
    readScript: 'Senaryoyu Oku',
    goToListeningPage: 'Dinleme Sayfasına Git',
    projectTypeLabel: 'Proje Türü',
    deleting: 'Siliniyor...',
    deleteProjectLabel: 'Projeyi Sil',
    analysisSections: 'Analiz Bölümleri',
    report: 'Rapor',
    scoring: 'Puanlama',
    characters: 'Karakterler',
    voiceover: 'Seslendirme',
    close: 'Kapat',
    processCompleted: 'İşlem Tamamlandı',
    processCompletedMessage: 'İşlem başarıyla tamamlandı',
    goToAnalysis: 'Analize Git',
    sequentialBreakdownNotCreated: 'Sıralı döküm henüz oluşturulmadı',
    generateSequentialBreakdownButton: 'Sıralı Döküm Oluştur',
    sequentialBreakdownCreated: 'Sıralı Döküm Oluşturuldu',
    sequentialBreakdownCreatedMessage: 'Sıralı döküm başarıyla oluşturuldu.',
    sequentialBreakdownFailed: 'Sıralı döküm oluşturulamadı',
    exportExcel: 'Excel İndir',
    downloadReport: 'Raporu İndir',
    downloadFailed: 'Rapor indirilemedi',
  },
  fr: {
    demo: 'Démo',
    pricing: 'Tarification',
    blog: 'Blog',
    signIn: 'Se connecter',
    startFree: 'Commencer gratuitement',
    hero: 'Analyse de scénarios',
    heroSub: 'Accéléré par l\'IA',
    heroDesc: 'Analysez vos œuvres en détail avec notre plateforme alimentée par l\'IA. Découvrez les personnages, l\'intrigue, le dialogue et bien plus encore avec des évaluations professionnelles.',
    startNow: 'Commencer maintenant',
    seeDemo: 'Voir la démo',
    fastAnalysis: 'Analyse rapide',
    fastAnalysisDesc: 'Analysez vos œuvres en quelques minutes. Obtenez des rapports détaillés grâce à la technologie de l\'IA.',
    detailedReports: 'Rapports détaillés',
    detailedReportsDesc: 'Obtenez des informations approfondies sur l\'analyse des personnages, l\'intrigue, la qualité du dialogue et bien plus encore.',
    secure: 'Sécurisé et privé',
    secureDesc: 'Vos données sont complètement sûres et privées. Seul vous et April AI pouvez accéder à vos données d\'analyse.',
    firstFree: 'Première analyse gratuite',
    firstFreeDesc: 'Analysez une œuvre complètement sans carte de crédit. Commencez maintenant.',
    tryFree: 'Essayer gratuitement',
    copyright: '© 2025 April AI. Tous droits réservés.',
    back: 'Retour',
    analyzeTitle: 'Analyses',
    viewAnalyses: 'Aller aux analyses',
    // Upload Page
    newAnalysis: 'Créer une nouvelle analyse',
    newAnalysisDesc: 'Téléchargez votre scénario et lancez le processus d\'analyse alimenté par l\'IA',
    uploadPdf: 'Télécharger le fichier PDF',
    dragDropPdf: 'Glissez-déposez votre fichier PDF ici',
    orClickToSelect: 'ou cliquez pour sélectionner un fichier',
    selectFile: 'Sélectionner le fichier',
    maxFileSize: 'Taille maximale du fichier : 50 Mo',
    changeFile: 'Changer le fichier',
    fileTooLarge: 'Le fichier est trop volumineux. Taille maximale du fichier : 50 Mo',
    contentType: 'Type de contenu',
    filmScript: 'Scénario de film',
    seriesScript: 'Scénario de série',
    analysisOptions: 'Options d\'analyse',
    generateReport: 'Générer un rapport',
    generateCharacterBreakdown: 'Répartition des personnages',
    sequentialBreakdown: 'Décomposition séquentielle',
    reportLanguage: 'Langue du rapport',
    uploading: 'Téléchargement...',
    uploadAndAnalyze: 'Télécharger et analyser',
    analysisCompleted: 'Analyse terminée',
    analysisCompletedDesc: 'L\'analyse et les sorties supplémentaires sélectionnées ont été créées. Cliquez sur le bouton ci-dessous pour voir les détails de l\'analyse.',
    viewAnalysis: 'Voir l\'analyse',
    reports: 'Rapports',
    loading: 'Chargement',
    processing: 'Traitement',
    analyzing: 'Analyse en cours, veuillez patienter...',
    // Additional upload page translations
    file: 'Fichier',
    analysis: 'Analyse',
    summary: 'Résumé',
    options: 'Options',
    startAnalysis: 'Démarrer l\'analyse',
    series: 'Série',
    film: 'Film',
    character: 'Personnage',
    pleaseSelectFile: 'Veuillez sélectionner un fichier',
    pleaseSelectContentType: 'Veuillez sélectionner le type de contenu',
    pleaseSelectAtLeastOneOutput: '⚠️ Veuillez sélectionner au moins une sortie!',
    readingPdf: '📄 Lecture du fichier PDF...',
    creatingCharacterBreakdown: '🎭 Création de la répartition des personnages...',
    uploadFailed: 'Échec du téléchargement',
    backendNotResponding: 'Le backend ne répond pas (timeout). Veuillez vérifier si le backend est en cours d\'exécution.',
    backendConnectionError: 'Erreur de connexion au backend. Veuillez vérifier si le backend est en cours d\'exécution. (http://localhost:8000)',
    authorizationError: 'Erreur d\'autorisation. Veuillez vous reconnecter.',
    invalidFileFormat: 'Format de fichier invalide. Veuillez télécharger un fichier PDF.',
    allOperationsCompleted: 'Toutes les opérations ont été complétées avec succès!',
    viewAnalysisResults: '📊 Voir les résultats de l\'analyse',
    // Navbar (use EN as fallback for untranslated)
    faq: 'FAQ',
    newProject: 'Nouveau Projet',
    tokenLabel: 'Jetons',
    admin: 'Admin',
    logout: 'Déconnexion',
    userListFailed: 'Failed to load user list',
    statsFailed: 'Failed to load statistics',
    userUpdated: 'User updated',
    userUpdateFailed: 'User update failed',
    adminPanel: 'Admin Panel',
    users: 'Users',
    project: 'Project',
    noEmail: 'No email',
    noUsers: 'No users found',
    userInfo: 'User Info',
    username: 'Username',
    fullName: 'Full Name',
    email: 'Email',
    roles: 'Roles',
    rolesHint: 'comma separated',
    newPassword: 'New Password',
    leaveBlankForNoChange: 'leave blank for no change',
    saving: 'Saving',
    save: 'Save',
    selectUserFromLeft: 'Select a user from the left list',
    userStatistics: 'User Statistics',
    totalProjects: 'Total Projects',
    averageScore: 'Average Score',
    highestScore: 'Highest Score',
    lowestScore: 'Lowest Score',
    statsNotFound: 'No statistics found',
    statsWillAppear: 'Statistics will appear here',
    deleteConfirm: 'Are you sure you want to delete {name}?',
    projectDeleted: 'Project deleted successfully',
    projectDeleteFailed: 'Failed to delete project',
    sortByDate: 'Sort by Date',
    sortByScore: 'Sort by Score',
    sortByName: 'Sort by Name',
    search: 'Search',
    myProjects: 'My Projects',
    projects: 'Projects',
    sort: 'Sort',
    workName: 'Work Name',
    type: 'Type',
    uploadDate: 'Upload Date',
    overallScore: 'Overall Score',
    status: 'Status',
    actions: 'Actions',
    analyzed: 'Analyzed',
    noProjectsFound: 'No projects found',
    faqTitle: 'Frequently Asked Questions',
    faqSubtitle: 'Find answers to common questions',
    loginError: 'Login failed. Please check your credentials.',
    login: 'Login',
    accountLogin: 'Login to your account',
    password: 'Password',
    loginInProgress: 'Logging in...',
    loginButton: 'Log In',
    freePlan: 'Free Plan',
    freePlanDesc: 'Perfect for trying',
    freeFeatures: ['1 Free Analysis', 'Basic Report', 'Email Support'],
    basicPlan: 'Basic',
    basicPlanDesc: 'For individuals',
    basicFeatures: ['10 Analysis/month', 'Detailed Reports', 'Priority Support'],
    getStarted: 'Get Started',
    proPlan: 'Pro',
    proPlanDesc: 'For professionals',
    proFeatures: ['Unlimited Analysis', 'Advanced Reports', '24/7 Support'],
    enterprisePlan: 'Enterprise',
    enterprisePlanDesc: 'For teams',
    enterpriseFeatures: ['Unlimited Everything', 'Custom Integration', 'Dedicated Support'],
    pricingTitle: 'Simple, Transparent Pricing',
    pricingSubtitle: 'Choose the plan that suits your needs',
    monthly: 'Monthly',
    yearly: 'Yearly',
    perMonth: '/month',
    perYear: '/year',
    mostPopular: 'Most Popular',
    deleteConfirmMessage: 'Are you sure you want to delete "{name}"?',
    projectDetails: 'Project Details',
    backToDashboard: 'Back to Dashboard',
    projectNotFound: 'Project not found',
    overview: 'Overview',
    generalScore: 'General Score',
    format: 'Format',
    logline: 'Logline',
    genisOzet: 'Summary',
    loadingAnalysis: 'Loading analysis...',
    noAnalysisSections: 'No analysis sections available',
    downloadFullAnalysisPdf: 'Download Full Analysis PDF',
    characterBreakdownNotCreated: 'Character breakdown not created yet',
    generatingCharacterBreakdown: 'Generating character breakdown...',
    characterBreakdownCreated: 'Character Breakdown Created',
    characterBreakdownCreatedMessage: 'Character breakdown has been successfully created',
    characterBreakdownFailed: 'Failed to generate character breakdown',
    generating: 'Generating...',
    generateCharacterBreakdownButton: 'Generate Character Breakdown',
    characterBreakdownTitle: 'Character Breakdown',
    sceneReplica: 'Scenes / Replicas',
    characterSceneCount: 'Total Scenes',
    characterInteractionBreakdown: 'Character Interactions',
    replicas: 'Replicas',
    scene: 'Scene',
    noDialogue: 'No dialogue',
    voiceoverTitle: 'Voiceover',
    simpleScriptReading: 'Simple Script Reading',
    simpleScriptReadingDesc: 'Convert your script to audio',
    reading: 'Reading...',
    readScript: 'Read Script',
    goToListeningPage: 'Go to Listening Page',
    projectTypeLabel: 'Project Type',
    deleting: 'Deleting...',
    deleteProjectLabel: 'Delete Project',
    analysisSections: 'Analysis Sections',
    report: 'Report',
    scoring: 'Scoring',
    characters: 'Characters',
    voiceover: 'Voiceover',
    close: 'Close',
    processCompleted: 'Process Completed',
    processCompletedMessage: 'Process completed successfully',
    goToAnalysis: 'Go to Analysis',
    sequentialBreakdownNotCreated: 'Décomposition séquentielle non créée',
    generateSequentialBreakdownButton: 'Générer la décomposition séquentielle',
    sequentialBreakdownCreated: 'Décomposition séquentielle créée',
    sequentialBreakdownCreatedMessage: 'La décomposition séquentielle a été créée avec succès.',
    sequentialBreakdownFailed: 'Échec de la génération de la décomposition séquentielle',
    exportExcel: 'Exporter Excel',
    downloadReport: 'Télécharger le rapport',
    downloadFailed: 'Échec du téléchargement',
  },
  es: {
    ...enTranslations, // Fallback to EN for untranslated keys
    demo: 'Demo',
    pricing: 'Precios',
    blog: 'Blog',
    signIn: 'Iniciar sesión',
    startFree: 'Comenzar gratis',
    hero: 'Análisis de guiones',
    heroSub: 'Acelerado por IA',
    heroDesc: 'Analiza tus obras en detalle con nuestra plataforma impulsada por IA. Descubre personajes, trama, diálogos y más con evaluaciones profesionales.',
    startNow: 'Comenzar ahora',
    seeDemo: 'Ver demostración',
    fastAnalysis: 'Análisis rápido',
    fastAnalysisDesc: 'Analiza tus obras en minutos. Obtén informes detallados gracias a la tecnología de IA.',
    detailedReports: 'Informes detallados',
    detailedReportsDesc: 'Obtén información profunda sobre análisis de personajes, trama, calidad del diálogo y mucho más.',
    secure: 'Seguro y privado',
    secureDesc: 'Tus datos están completamente seguros y privados. Solo tú y April AI pueden acceder a tus datos de análisis.',
    firstFree: 'Primer análisis gratis',
    firstFreeDesc: 'Analiza una obra completa sin tarjeta de crédito. Comienza ahora.',
    tryFree: 'Probar gratis',
    copyright: '© 2025 April AI. Todos los derechos reservados.',
    back: 'Atrás',
    analyzeTitle: 'Análisis',
    viewAnalyses: 'Ir a análisis',
    // Upload Page
    newAnalysis: 'Crear nuevo análisis',
    newAnalysisDesc: 'Sube tu guión e inicia el proceso de análisis impulsado por IA',
    uploadPdf: 'Subir archivo PDF',
    dragDropPdf: 'Arrastra y suelta tu archivo PDF aquí',
    orClickToSelect: 'o haz clic para seleccionar un archivo',
    selectFile: 'Seleccionar archivo',
    maxFileSize: 'Tamaño máximo del archivo: 50 MB',
    changeFile: 'Cambiar archivo',
    fileTooLarge: 'El archivo es demasiado grande. Tamaño máximo del archivo: 50 MB',
    contentType: 'Tipo de contenido',
    filmScript: 'Guión de película',
    seriesScript: 'Guión de serie',
    analysisOptions: 'Opciones de análisis',
    generateReport: 'Generar informe',
    generateCharacterBreakdown: 'Desglose de personajes',
    sequentialBreakdown: 'Desglose secuencial',
    reportLanguage: 'Idioma del informe',
    uploading: 'Subiendo...',
    uploadAndAnalyze: 'Subir y analizar',
    analysisCompleted: 'Análisis completado',
    analysisCompletedDesc: 'Se han creado el análisis y las salidas adicionales seleccionadas. Haz clic en el botón a continuación para ver los detalles del análisis.',
    viewAnalysis: 'Ver análisis',
    reports: 'Informes',
    loading: 'Cargando',
    processing: 'Procesando',
    analyzing: 'Analizando, por favor espere...',
    // Additional upload page translations
    file: 'Archivo',
    analysis: 'Análisis',
    summary: 'Resumen',
    options: 'Opciones',
    startAnalysis: 'Iniciar análisis',
    series: 'Serie',
    film: 'Película',
    character: 'Personaje',
    pleaseSelectFile: 'Por favor seleccione un archivo',
    pleaseSelectContentType: 'Por favor seleccione el tipo de contenido',
    pleaseSelectAtLeastOneOutput: '⚠️ Por favor seleccione al menos una salida!',
    readingPdf: '📄 Leyendo archivo PDF...',
    creatingCharacterBreakdown: '🎭 Creando desglose de personajes...',
    uploadFailed: 'Error al subir',
    backendNotResponding: 'El backend no responde (timeout). Por favor verifique si el backend está en ejecución.',
    backendConnectionError: 'Error de conexión al backend. Por favor verifique si el backend está en ejecución. (http://localhost:9000)',
    authorizationError: 'Error de autorización. Por favor inicie sesión nuevamente.',
    invalidFileFormat: 'Formato de archivo inválido. Por favor suba un archivo PDF.',
    allOperationsCompleted: '¡Todas las operaciones se completaron exitosamente!',
    viewAnalysisResults: '📊 Ver resultados del análisis',
  } as typeof enTranslations,
  ar: {
    ...enTranslations, // Fallback to EN
    demo: 'عرض توضيحي',
    pricing: 'الأسعار',
    blog: 'مدونة',
    signIn: 'تسجيل الدخول',
    startFree: 'ابدأ مجانًا',
    hero: 'تحليل السيناريوهات',
    heroSub: 'معجل بالذكاء الاصطناعي',
    heroDesc: 'حلل أعمالك بالتفصيل باستخدام منصتنا المدعومة بالذكاء الاصطناعي. اكتشف الشخصيات والحبكة والحوار والمزيد من خلال التقييمات الاحترافية.',
    startNow: 'ابدأ الآن',
    seeDemo: 'اعرض العرض التوضيحي',
    fastAnalysis: 'تحليل سريع',
    fastAnalysisDesc: 'حلل أعمالك في دقائق. احصل على تقارير مفصلة بفضل تكنولوجيا الذكاء الاصطناعي.',
    detailedReports: 'تقارير مفصلة',
    detailedReportsDesc: 'احصل على معلومات متعمقة حول تحليل الشخصيات والحبكة وجودة الحوار والمزيد.',
    secure: 'آمن وخاص',
    secureDesc: 'بيانتك آمنة تماما وخاصة. فقط أنت و April AI يمكنهما الوصول إلى بيانات تحليلك.',
    firstFree: 'التحليل الأول مجاني',
    firstFreeDesc: 'حلل عملا واحدا بشكل شامل بدون بطاقة ائتمان. ابدأ الآن.',
    tryFree: 'جرب مجانًا',
    copyright: '© 2025 April AI. جميع الحقوق محفوظة.',
    back: 'العودة',
    analyzeTitle: 'التحليلات',
    viewAnalyses: 'الذهاب إلى التحليلات',
    // Upload Page
    newAnalysis: 'إنشاء تحليل جديد',
    newAnalysisDesc: 'قم بتحميل السيناريو الخاص بك وابدأ عملية التحليل المدعومة بالذكاء الاصطناعي',
    uploadPdf: 'تحميل ملف PDF',
    dragDropPdf: 'اسحب وأفلت ملف PDF الخاص بك هنا',
    orClickToSelect: 'أو انقر لتحديد ملف',
    selectFile: 'تحديد الملف',
    maxFileSize: 'الحد الأقصى لحجم الملف: 50 ميجابايت',
    changeFile: 'تغيير الملف',
    fileTooLarge: 'الملف كبير جدًا. الحد الأقصى لحجم الملف: 50 ميجابايت',
    contentType: 'نوع المحتوى',
    filmScript: 'سيناريو فيلم',
    seriesScript: 'سيناريو مسلسل',
    analysisOptions: 'خيارات التحليل',
    generateReport: 'إنشاء تقرير',
    generateCharacterBreakdown: 'تفصيل الشخصيات',
    sequentialBreakdown: 'التفصيل التسلسلي',
    reportLanguage: 'لغة التقرير',
    uploading: 'جارٍ التحميل...',
    uploadAndAnalyze: 'تحميل وتحليل',
    analysisCompleted: 'اكتمل التحليل',
    analysisCompletedDesc: 'تم إنشاء التحليل والمخرجات الإضافية المحددة. انقر فوق الزر أدناه لعرض تفاصيل التحليل.',
    viewAnalysis: 'عرض التحليل',
    reports: 'التقارير',
    loading: 'جارٍ التحميل',
    processing: 'معالجة',
    analyzing: 'جارٍ التحليل، يرجى الانتظار...',
    // Additional upload page translations
    file: 'ملف',
    analysis: 'تحليل',
    summary: 'ملخص',
    options: 'خيارات',
    startAnalysis: 'بدء التحليل',
    series: 'مسلسل',
    film: 'فيلم',
    character: 'شخصية',
    pleaseSelectFile: 'يرجى تحديد ملف',
    pleaseSelectContentType: 'يرجى تحديد نوع المحتوى',
    pleaseSelectAtLeastOneOutput: '⚠️ يرجى تحديد مخرج واحد على الأقل!',
    readingPdf: '📄 قراءة ملف PDF...',
    creatingCharacterBreakdown: '🎭 إنشاء تفصيل الشخصيات...',
    uploadFailed: 'فشل التحميل',
    backendNotResponding: 'الخادم لا يستجيب (انتهت المهلة). يرجى التحقق مما إذا كان الخادم يعمل.',
    backendConnectionError: 'خطأ في الاتصال بالخادم. يرجى التحقق مما إذا كان الخادم يعمل. (http://localhost:8000)',
    authorizationError: 'خطأ في التفويض. يرجى تسجيل الدخول مرة أخرى.',
    invalidFileFormat: 'تنسيق الملف غير صالح. يرجى تحميل ملف PDF.',
    allOperationsCompleted: 'تم إكمال جميع العمليات بنجاح!',
    viewAnalysisResults: '📊 عرض نتائج التحليل',
  } as typeof enTranslations,
  de: {
    ...enTranslations, // Fallback to EN
    demo: 'Demo',
    pricing: 'Preise',
    blog: 'Blog',
    signIn: 'Anmelden',
    startFree: 'Kostenlos starten',
    hero: 'Drehbuchanalyse',
    heroSub: 'Beschleunigt mit KI',
    heroDesc: 'Analysieren Sie Ihre Werke im Detail mit unserer KI-gestützten Plattform. Entdecken Sie Charaktere, Handlung, Dialog und mehr mit professionellen Bewertungen.',
    startNow: 'Jetzt starten',
    seeDemo: 'Demo ansehen',
    fastAnalysis: 'Schnelle Analyse',
    fastAnalysisDesc: 'Analysieren Sie Ihre Werke in Minuten. Erhalten Sie detaillierte Berichte dank KI-Technologie.',
    detailedReports: 'Detaillierte Berichte',
    detailedReportsDesc: 'Erhalten Sie tiefgreifende Informationen zu Charakteranalyse, Handlung, Dialogqualität und mehr.',
    secure: 'Sicher & Privat',
    secureDesc: 'Ihre Daten sind vollständig sicher und privat. Nur Sie und April AI können auf Ihre Analysedaten zugreifen.',
    firstFree: 'Erste Analyse kostenlos',
    firstFreeDesc: 'Analysieren Sie ein Werk umfassend ohne Kreditkarte. Starten Sie jetzt.',
    tryFree: 'Kostenlos testen',
    copyright: '© 2025 April AI. Alle Rechte vorbehalten.',
    back: 'Zurück',
    analyzeTitle: 'Analysen',
    viewAnalyses: 'Zu Analysen gehen',
    // Upload Page
    newAnalysis: 'Neue Analyse erstellen',
    newAnalysisDesc: 'Laden Sie Ihr Drehbuch hoch und starten Sie den KI-gestützten Analyseprozess',
    uploadPdf: 'PDF-Datei hochladen',
    dragDropPdf: 'Ziehen Sie Ihre PDF-Datei hierher',
    orClickToSelect: 'oder klicken Sie, um eine Datei auszuwählen',
    selectFile: 'Datei auswählen',
    maxFileSize: 'Maximale Dateigröße: 50 MB',
    changeFile: 'Datei ändern',
    fileTooLarge: 'Datei ist zu groß. Maximale Dateigröße: 50 MB',
    contentType: 'Inhaltstyp',
    filmScript: 'Filmdrehbuch',
    seriesScript: 'Seriendrehbuch',
    analysisOptions: 'Analyseoptionen',
    generateReport: 'Bericht erstellen',
    generateCharacterBreakdown: 'Charakteraufschlüsselung',
    sequentialBreakdown: 'Sequenzielle Aufschlüsselung',
    reportLanguage: 'Berichtssprache',
    uploading: 'Wird hochgeladen...',
    uploadAndAnalyze: 'Hochladen und analysieren',
    analysisCompleted: 'Analyse abgeschlossen',
    analysisCompletedDesc: 'Analyse und Ihre ausgewählten zusätzlichen Ausgaben wurden erstellt. Klicken Sie auf die Schaltfläche unten, um Analyseeinzelheiten anzuzeigen.',
    viewAnalysis: 'Analyse anzeigen',
    reports: 'Berichte',
    loading: 'Wird geladen',
    processing: 'Wird verarbeitet',
    analyzing: 'Wird analysiert, bitte warten...',
    // Additional upload page translations
    file: 'Datei',
    analysis: 'Analyse',
    summary: 'Zusammenfassung',
    options: 'Optionen',
    startAnalysis: 'Analyse starten',
    series: 'Serie',
    film: 'Film',
    character: 'Charakter',
    pleaseSelectFile: 'Bitte wählen Sie eine Datei aus',
    pleaseSelectContentType: 'Bitte wählen Sie den Inhaltstyp aus',
    pleaseSelectAtLeastOneOutput: '⚠️ Bitte wählen Sie mindestens eine Ausgabe aus!',
    readingPdf: '📄 PDF-Datei wird gelesen...',
    creatingCharacterBreakdown: '🎭 Charakteraufschlüsselung wird erstellt...',
    uploadFailed: 'Upload fehlgeschlagen',
    backendNotResponding: 'Backend antwortet nicht (Timeout). Bitte überprüfen Sie, ob das Backend läuft.',
    backendConnectionError: 'Backend-Verbindungsfehler. Bitte überprüfen Sie, ob das Backend läuft. (http://localhost:8000)',
    authorizationError: 'Autorisierungsfehler. Bitte melden Sie sich erneut an.',
    invalidFileFormat: 'Ungültiges Dateiformat. Bitte laden Sie eine PDF-Datei hoch.',
    allOperationsCompleted: 'Alle Operationen wurden erfolgreich abgeschlossen!',
    viewAnalysisResults: '📊 Analyseergebnisse anzeigen',
  } as typeof enTranslations,
  it: {
    ...enTranslations, // Fallback to EN
    demo: 'Demo',
    pricing: 'Prezzi',
    blog: 'Blog',
    signIn: 'Accedi',
    startFree: 'Inizia gratuitamente',
    hero: 'Analisi di sceneggiature',
    heroSub: 'Accelerato con AI',
    heroDesc: 'Analizza le tue opere in dettaglio con la nostra piattaforma alimentata dall\'AI. Scopri personaggi, trama, dialoghi e altro ancora con valutazioni professionali.',
    startNow: 'Inizia ora',
    seeDemo: 'Vedi demo',
    fastAnalysis: 'Analisi rapida',
    fastAnalysisDesc: 'Analizza le tue opere in pochi minuti. Ottieni rapporti dettagliati grazie alla tecnologia AI.',
    detailedReports: 'Rapporti dettagliati',
    detailedReportsDesc: 'Ottieni informazioni approfondite sull\'analisi dei personaggi, trama, qualità del dialogo e altro ancora.',
    secure: 'Sicuro e privato',
    secureDesc: 'I tuoi dati sono completamente sicuri e privati. Solo tu e April AI potete accedere ai tuoi dati di analisi.',
    firstFree: 'Prima analisi gratuita',
    firstFreeDesc: 'Analizza un\'opera completamente senza carta di credito. Inizia ora.',
    tryFree: 'Prova gratuitamente',
    copyright: '© 2025 April AI. Tutti i diritti riservati.',
    back: 'Indietro',
    analyzeTitle: 'Analisi',
    viewAnalyses: 'Vai alle analisi',
    // Upload Page
    newAnalysis: 'Crea nuova analisi',
    newAnalysisDesc: 'Carica la tua sceneggiatura e avvia il processo di analisi alimentato dall\'IA',
    uploadPdf: 'Carica file PDF',
    dragDropPdf: 'Trascina e rilascia il tuo file PDF qui',
    orClickToSelect: 'o fai clic per selezionare un file',
    selectFile: 'Seleziona file',
    maxFileSize: 'Dimensione massima del file: 50 MB',
    changeFile: 'Cambia file',
    fileTooLarge: 'Il file è troppo grande. Dimensione massima del file: 50 MB',
    contentType: 'Tipo di contenuto',
    filmScript: 'Sceneggiatura cinematografica',
    seriesScript: 'Sceneggiatura di serie',
    analysisOptions: 'Opzioni di analisi',
    generateReport: 'Genera rapporto',
    generateCharacterBreakdown: 'Scomposizione dei personaggi',
    sequentialBreakdown: 'Scomposizione sequenziale',
    reportLanguage: 'Lingua del rapporto',
    uploading: 'Caricamento...',
    uploadAndAnalyze: 'Carica e analizza',
    analysisCompleted: 'Analisi completata',
    analysisCompletedDesc: 'L\'analisi e le uscite aggiuntive selezionate sono state create. Fai clic sul pulsante qui sotto per visualizzare i dettagli dell\'analisi.',
    viewAnalysis: 'Visualizza analisi',
    reports: 'Rapporti',
    loading: 'Caricamento',
    processing: 'Elaborazione',
    analyzing: 'Analisi in corso, attendere prego...',
    // Additional upload page translations
    file: 'File',
    analysis: 'Analisi',
    summary: 'Riepilogo',
    options: 'Opzioni',
    startAnalysis: 'Avvia analisi',
    series: 'Serie',
    film: 'Film',
    character: 'Personaggio',
    pleaseSelectFile: 'Si prega di selezionare un file',
    pleaseSelectContentType: 'Si prega di selezionare il tipo di contenuto',
    pleaseSelectAtLeastOneOutput: '⚠️ Si prega di selezionare almeno un output!',
    readingPdf: '📄 Lettura del file PDF...',
    creatingCharacterBreakdown: '🎭 Creazione della scomposizione dei personaggi...',
    uploadFailed: 'Caricamento fallito',
    backendNotResponding: 'Il backend non risponde (timeout). Si prega di verificare se il backend è in esecuzione.',
    backendConnectionError: 'Errore di connessione al backend. Si prega di verificare se il backend è in esecuzione. (http://localhost:8000)',
    authorizationError: 'Errore di autorizzazione. Si prega di accedere nuovamente.',
    invalidFileFormat: 'Formato file non valido. Si prega di caricare un file PDF.',
    allOperationsCompleted: 'Tutte le operazioni sono state completate con successo!',
    viewAnalysisResults: '📊 Visualizza risultati dell\'analisi',
  } as typeof enTranslations,
  ru: {
    ...enTranslations, // Fallback to EN
    demo: 'Демо',
    pricing: 'Цены',
    blog: 'Блог',
    signIn: 'Войти',
    startFree: 'Начать бесплатно',
    hero: 'Анализ сценариев',
    heroSub: 'Ускорено с помощью ИИ',
    heroDesc: 'Детально анализируйте свои произведения с помощью нашей платформы на базе ИИ. Откройте для себя персонажей, сюжет, диалоги и многое другое с профессиональными оценками.',
    startNow: 'Начать сейчас',
    seeDemo: 'Посмотреть демо',
    fastAnalysis: 'Быстрый анализ',
    fastAnalysisDesc: 'Анализируйте свои произведения за считанные минуты. Получайте подробные отчеты благодаря технологии ИИ.',
    detailedReports: 'Подробные отчеты',
    detailedReportsDesc: 'Получайте глубокую информацию об анализе персонажей, сюжете, качестве диалогов и многом другом.',
    secure: 'Безопасно и конфиденциально',
    secureDesc: 'Ваши данные полностью защищены и конфиденциальны. Только вы и April AI можете получить доступ к вашим данным анализа.',
    firstFree: 'Первый анализ бесплатно',
    firstFreeDesc: 'Полностью проанализируйте одно произведение без кредитной карты. Начните сейчас.',
    tryFree: 'Попробовать бесплатно',
    copyright: '© 2025 April AI. Все права защищены.',
    back: 'Назад',
    analyzeTitle: 'Анализы',
    viewAnalyses: 'Перейти к анализам',
    // Upload Page
    newAnalysis: 'Создать новый анализ',
    newAnalysisDesc: 'Загрузите свой сценарий и запустите процесс анализа на базе ИИ',
    uploadPdf: 'Загрузить PDF файл',
    dragDropPdf: 'Перетащите ваш PDF файл сюда',
    orClickToSelect: 'или нажмите, чтобы выбрать файл',
    selectFile: 'Выбрать файл',
    maxFileSize: 'Максимальный размер файла: 50 МБ',
    changeFile: 'Изменить файл',
    fileTooLarge: 'Файл слишком большой. Максимальный размер файла: 50 МБ',
    contentType: 'Тип контента',
    filmScript: 'Сценарий фильма',
    seriesScript: 'Сценарий сериала',
    analysisOptions: 'Параметры анализа',
    generateReport: 'Создать отчет',
    generateCharacterBreakdown: 'Разбивка персонажей',
    sequentialBreakdown: 'Последовательная разбивка',
    reportLanguage: 'Язык отчета',
    uploading: 'Загрузка...',
    uploadAndAnalyze: 'Загрузить и проанализировать',
    analysisCompleted: 'Анализ завершен',
    analysisCompletedDesc: 'Анализ и выбранные дополнительные выходные данные созданы. Нажмите кнопку ниже, чтобы просмотреть детали анализа.',
    viewAnalysis: 'Просмотреть анализ',
    reports: 'Отчеты',
    loading: 'Загрузка',
    processing: 'Обработка',
    analyzing: 'Анализ выполняется, пожалуйста, подождите...',
    // Additional upload page translations
    file: 'Файл',
    analysis: 'Анализ',
    summary: 'Сводка',
    options: 'Опции',
    startAnalysis: 'Начать анализ',
    series: 'Сериал',
    film: 'Фильм',
    character: 'Персонаж',
    pleaseSelectFile: 'Пожалуйста, выберите файл',
    pleaseSelectContentType: 'Пожалуйста, выберите тип контента',
    pleaseSelectAtLeastOneOutput: '⚠️ Пожалуйста, выберите хотя бы один выход!',
    readingPdf: '📄 Чтение PDF файла...',
    creatingCharacterBreakdown: '🎭 Создание разбивки персонажей...',
    uploadFailed: 'Ошибка загрузки',
    backendNotResponding: 'Backend не отвечает (тайм-аут). Пожалуйста, проверьте, работает ли backend.',
    backendConnectionError: 'Ошибка подключения к backend. Пожалуйста, проверьте, работает ли backend. (http://localhost:8000)',
    authorizationError: 'Ошибка авторизации. Пожалуйста, войдите снова.',
    invalidFileFormat: 'Неверный формат файла. Пожалуйста, загрузите PDF файл.',
    allOperationsCompleted: 'Все операции успешно завершены!',
    viewAnalysisResults: '📊 Просмотреть результаты анализа',
  } as typeof enTranslations,
};

type Translations = typeof translations.en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language') as Language | null;
    return saved || 'tr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
